import React, { useState, useEffect } from 'react';
import { X, Save, ExternalLink, Plus, Trash2 } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface ToolMetadataModalProps {
  platformType: string;
  toolName: string;
  onClose: () => void;
  onSaved: (toolName: string) => void;
}

interface ToolMetadata {
  id?: number;
  platform_type: string;
  tool_name: string;
  description: string;
  use_cases: string[];
  license_info: string;
  documentation_url: string;
  ai_opportunities: string;
  regulatory_notes: string;
}

const ToolMetadataModal: React.FC<ToolMetadataModalProps> = ({
  platformType,
  toolName,
  onClose,
  onSaved
}) => {
  const isNewTool = !toolName;

  const [formData, setFormData] = useState<ToolMetadata>({
    platform_type: platformType,
    tool_name: toolName || '',
    description: '',
    use_cases: [],
    license_info: '',
    documentation_url: '',
    ai_opportunities: '',
    regulatory_notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [newUseCase, setNewUseCase] = useState('');

  useEffect(() => {
    if (!isNewTool) {
      loadMetadata();
    }
  }, [toolName]);

  const loadMetadata = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/workflow-tool-metadata/platform/${platformType}`);

      if (response.data.success) {
        const metadata = response.data.data.find((m: any) => m.tool_name === toolName);
        if (metadata) {
          setFormData({
            ...metadata,
            use_cases: metadata.use_cases || []
          });
        }
      }
    } catch (error) {
      console.error('Error loading tool metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.tool_name.trim()) {
      toast.error('Tool name is required');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        platform_type: platformType
      };

      let response;
      if (formData.id) {
        response = await api.put(`/api/workflow-tool-metadata/${formData.id}`, payload);
      } else {
        response = await api.post('/api/workflow-tool-metadata', payload);
      }

      if (response.data.success) {
        toast.success(`Tool metadata ${formData.id ? 'updated' : 'created'} successfully`);
        onSaved(formData.tool_name);
      }
    } catch (error: any) {
      console.error('Error saving tool metadata:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save tool metadata';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addUseCase = () => {
    if (newUseCase.trim() && !formData.use_cases.includes(newUseCase.trim())) {
      setFormData(prev => ({
        ...prev,
        use_cases: [...prev.use_cases, newUseCase.trim()]
      }));
      setNewUseCase('');
    }
  };

  const removeUseCase = (index: number) => {
    setFormData(prev => ({
      ...prev,
      use_cases: prev.use_cases.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">
            {isNewTool ? 'Add New Tool' : `Edit Tool: ${toolName}`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Tool Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tool Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.tool_name}
              onChange={(e) => setFormData(prev => ({ ...prev, tool_name: e.target.value }))}
              disabled={!isNewTool}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              placeholder="e.g., Flow Builder, Power Automate"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe what this tool does..."
            />
          </div>

          {/* Use Cases */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Use Cases
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newUseCase}
                onChange={(e) => setNewUseCase(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addUseCase()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a use case..."
              />
              <button
                onClick={addUseCase}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            {formData.use_cases.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.use_cases.map((useCase, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg"
                  >
                    <span className="text-sm text-gray-700">{useCase}</span>
                    <button
                      onClick={() => removeUseCase(index)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* License Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              License Information
            </label>
            <input
              type="text"
              value={formData.license_info}
              onChange={(e) => setFormData(prev => ({ ...prev, license_info: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Included with subscription, Requires license"
            />
          </div>

          {/* Documentation URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Documentation URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.documentation_url}
                onChange={(e) => setFormData(prev => ({ ...prev, documentation_url: e.target.value }))}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://docs.example.com/tool-name"
              />
              {formData.documentation_url && (
                <a
                  href={formData.documentation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-blue-600 hover:text-blue-800 flex items-center"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* AI Opportunities */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              AI/ML Opportunities
            </label>
            <textarea
              value={formData.ai_opportunities}
              onChange={(e) => setFormData(prev => ({ ...prev, ai_opportunities: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Describe AI/ML integration opportunities..."
            />
          </div>

          {/* Regulatory Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Regulatory & Compliance Notes
            </label>
            <textarea
              value={formData.regulatory_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, regulatory_notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Important compliance and regulatory considerations..."
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !formData.tool_name.trim()}
            className="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Saving...' : 'Save Tool'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolMetadataModal;
